/**
 * theme.addToCartAndRedirect — the landing pages' add-to-cart path, called by ~57
 * sections. The name is kept because those sections call it by it; what it does at
 * the end has changed.
 *
 * Ported from the main theme's assets/theme.js, then diverged on purpose: where the
 * source navigated to /cart, this opens the cart drawer instead and leaves the buyer
 * on the page they were reading. A landing page is a single argument, and sending
 * the reader to another page in the middle of it throws that away.
 *
 * It lives in its own file because this theme has no theme.js bundle (D22), so
 * the migrated sections arrived calling a function nothing defined.
 *
 * The drawer is not addressed directly. It listens on document for the same two
 * events the product form publishes, so this announces and the drawer answers:
 *
 *   biosphere:cart:adding   before the request — the drawer opens straight away and
 *                           the wait happens inside it, where the result will appear.
 *   biosphere:cart:change   after it, carrying the fresh cart. `source` is this file,
 *                           so every other surface re-renders and none echoes back.
 *
 * Pages with no drawer in their layout still navigate, so nothing depends on it.
 *
 * The discount is applied BEFORE the add on purpose: applying it afterwards left
 * the cart showing full price until something forced a recalculation.
 *
 * Idempotent — linked by every section that needs it, so it may run more than once.
 */
window.theme = window.theme || {};

/**
 * Blocks double-tap adds: calls made while one is running get the same promise, so a
 * second tap cannot post a second line. Released once the add settles — it used to be
 * held on success because the page was leaving for /cart, and now it is not.
 */
var inFlightAdd = null;

/**
 * The wait state a section puts its CTA into while an add runs, so it can be undone
 * when the add finishes.
 *
 * Every section marks the wait and then relies on the navigation to /cart to clear
 * it. Nothing navigates any more, so without this the CTA sits spinning for good and
 * the buyer cannot click it again without reloading.
 *
 * The sections do not mark it the same way — between them they set `disabled`, add a
 * `loading` or `is-loading` class, and write inline `pointer-events` and `opacity` —
 * so rather than guessing, the state is photographed before the section touches it
 * and put back afterwards. Only what changed is restored, which leaves a genuinely
 * sold-out button disabled and a flavour still selected.
 */
var LP_ATC_WAIT_CLASSES = ['loading', 'is-loading'];
var LP_ATC_STATEFUL = 'button, a, [data-supply-option]';

function lpAtcSnapshot(root) {
  var state = new Map();
  root.querySelectorAll(LP_ATC_STATEFUL).forEach(function (el) {
    state.set(el, {
      disabled: !!el.disabled,
      pointerEvents: el.style.pointerEvents,
      opacity: el.style.opacity,
      classes: LP_ATC_WAIT_CLASSES.filter(function (name) {
        return el.classList.contains(name);
      })
    });
  });
  return state;
}

if (!theme._lpAtcResetWired) {
  theme._lpAtcResetWired = true;
  theme._lpAtcWaitState = null;
  theme._lpAtcPending = 0;

  document.addEventListener(
    'click',
    function (event) {
      /* Opening the drawer clicks its own trigger, and that click lands after the CTA
         is already waiting. Photographing then would record the wait as the normal
         state and leave it stuck, so clicks during an add are not snapshots. */
      if (theme._lpAtcPending) return;

      /* Only the section that was clicked: a CTA never puts another one to sleep. */
      var target = event.target instanceof Element ? event.target : null;
      var root = (target && target.closest('.shopify-section')) || document.body;
      theme._lpAtcWaitState = lpAtcSnapshot(root);
    },
    true
  );
}

/** Put the clicked section back the way it was before the add. */
theme._lpAtcRestoreButtons = function () {
  var before = theme._lpAtcWaitState;
  if (!before) return;

  before.forEach(function (was, el) {
    if (!el.isConnected) return;

    if (el.disabled && !was.disabled) el.disabled = false;
    if (el.style.pointerEvents !== was.pointerEvents) el.style.pointerEvents = was.pointerEvents;
    if (el.style.opacity !== was.opacity) el.style.opacity = was.opacity;

    LP_ATC_WAIT_CLASSES.forEach(function (name) {
      if (el.classList.contains(name) && was.classes.indexOf(name) === -1) {
        el.classList.remove(name);
      }
    });
  });
};

/**
 * Apply the LP discount without following a discount link to the full homepage.
 * Read and merge the cart's codes: cart/update replaces the entire code list.
 * If that list cannot be read safely, retain the discount-link behaviour, but
 * redirect it to the small cart JSON response rather than the homepage.
 */
function lpApplyDiscount(code) {
  if (!code) return Promise.resolve();
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var cartJsonUrl = root + 'cart.js';
  var normalised = String(code).toLowerCase();

  function fallback() {
    return fetch(root + 'discount/' + encodeURIComponent(code) +
      '?redirect=' + encodeURIComponent(cartJsonUrl), { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Could not apply discount: ' + response.status);
      });
  }

  return fetch(cartJsonUrl, {
    cache: 'no-store',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' }
  })
    .then(function (response) {
      if (!response.ok) throw new Error('Could not read cart: ' + response.status);
      return response.json();
    })
    .then(function (cart) {
      // Do not mistake a missing/changed response field for an empty code list.
      if (!Array.isArray(cart.discount_codes)) throw new Error('Missing cart discount codes');
      var codes = cart.discount_codes.map(function (discount) {
        if (!discount || typeof discount.code !== 'string') throw new Error('Invalid cart discount code');
        return discount.code;
      });
      if (codes.some(function (existing) { return existing.toLowerCase() === normalised; })) return;
      codes.push(code);
      return fetch(root + 'cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(',') })
      }).then(function (response) {
        if (!response.ok) throw new Error('Could not update discount: ' + response.status);
        return response.json();
      });
    })
    .catch(fallback);
}

theme.addToCartAndRedirect = function (opts) {
  if (inFlightAdd) return inFlightAdd;

  opts = opts || {};
  var variantId = opts.variantId;
  var quantity = opts.quantity || 1;
  /* Several lines in one add, ported from biosphere-combo-buy.js: one form cannot add
     two line items, so the request becomes a JSON {items} POST. */
  var items = Array.isArray(opts.items) && opts.items.length ? opts.items : null;
  var discountCode = opts.discountCode || '';
  var addUrl = opts.addUrl || '/cart/add.js';
  var cartUrl = opts.cartUrl || '/cart';

  if (!variantId && !items) {
    return Promise.reject(new Error('theme.addToCartAndRedirect: variantId is required'));
  }

  /* A sold-out variant rejects rather than posting: the caller re-enables its
     button in the .catch, which is how the landing CTAs recover. */
  if (opts.available === false) {
    return Promise.reject(new Error('theme.addToCartAndRedirect: variant is sold out'));
  }

  /* Looked up per call, not once: the drawer is rendered by the layout, but a section
     may run this before the element upgrades. */
  var hasDrawer = !!document.querySelector('biosphere-cart-drawer');

  theme._lpAtcPending += 1;

  if (hasDrawer) {
    document.dispatchEvent(
      new CustomEvent('biosphere:cart:adding', { detail: { source: 'lp-add-to-cart' } })
    );
  }

  /* Whatever happens — added, rejected, offline — the wait state ends here, because
     nothing navigates away to clear it any more. */
  var settle = function (result) {
    theme._lpAtcPending = Math.max(0, theme._lpAtcPending - 1);
    theme._lpAtcRestoreButtons();
    return result;
  };

  var applyDiscount = lpApplyDiscount(discountCode);

  inFlightAdd = applyDiscount
    .then(function () {
      if (items) {
        return fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        });
      }

      var fd = new FormData();
      fd.append('id', variantId);
      fd.append('quantity', quantity);
      return fetch(addUrl, { method: 'POST', body: fd });
    })
    .then(function (r) {
      return r.json();
    })
    .then(function (data) {
      if (!hasDrawer) {
        /* Still navigating, so the wait state travels with the page. */
        window.location.href = cartUrl;
        return data;
      }

      /* The add response is the line, not the cart, and the drawer needs totals.
         A failed read still publishes, so the drawer re-renders from the server
         rather than sitting open on stale contents. */
      return fetch(cartUrl + '.js', { cache: 'no-store', headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.json();
        })
        .catch(function () {
          return null;
        })
        .then(function (cart) {
          document.dispatchEvent(
            new CustomEvent('biosphere:cart:change', {
              detail: { cart: cart, action: 'add', item: data, source: 'lp-add-to-cart' }
            })
          );
          inFlightAdd = null;
          return settle(data);
        });
    })
    .catch(function (error) {
      inFlightAdd = null;
      settle();
      /* The drawer opened on cart:adding and only cart:change releases it, so a
         failed add publishes too — with no cart, which every subscriber guards. */
      if (hasDrawer) {
        document.dispatchEvent(
          new CustomEvent('biosphere:cart:change', {
            detail: { cart: null, action: 'update', source: 'lp-add-to-cart' }
          })
        );
      }
      throw error;
    });

  return inFlightAdd;
};
